import { Alert as NovaAlert, AlertTitle as NovaAlertTitle } from '@fme-nova/components'
import { Alert as MuiAlert, AlertProps, AlertTitle as MuiAlertTitle, AlertTitleProps } from '@mui/material'
import React from 'react'

import { createComponentWrapperWithRef, styled } from '../../theme'
import { COLORS } from '../../theme/colors'

type MuiAlertVariant = 'standard' | 'filled' | 'outlined'
type NovaAlertVariant = MuiAlertVariant | 'actionable'

export type BaseAlertProps = Omit<AlertProps, 'variant'> & {
  variant?: NovaAlertVariant
}

const MuiBaseAlert = styled(MuiAlert)`
  border-radius: 8px;
  white-space: pre-wrap;

  &.MuiAlert-standardError {
    border: 1px solid ${COLORS.error.outlinedBorder};
    background: ${COLORS.error.light};
    color: ${COLORS.error.main};

    & .MuiAlert-icon {
      color: ${COLORS.error.main};
    }
  }

  &.MuiAlert-standardWarning {
    border: 1px solid ${COLORS.warning.outlinedBorder};
    background: ${COLORS.warning.light};
    color: ${COLORS.warning.main};

    & .MuiAlert-icon {
      color: ${COLORS.warning.main};
    }
  }

  &.MuiAlert-standardInfo {
    border: 1px solid ${COLORS.info.outlinedBorder};
    background: ${COLORS.info.light};
    color: ${COLORS.info.main};

    & .MuiAlert-icon {
      color: ${COLORS.info.main};
    }
  }

  &.MuiAlert-standardSuccess {
    border: 1px solid ${COLORS.success.outlinedBorder};
    background: ${COLORS.success.light};
    color: ${COLORS.success.main};

    & .MuiAlert-icon {
      color: ${COLORS.success.main};
    }
  }

  &.MuiAlert-outlinedError {
    border: 1px solid ${COLORS.error.main};
    color: ${COLORS.error.main};

    & .MuiAlert-icon {
      color: ${COLORS.error.main};
    }
  }

  &.MuiAlert-outlinedWarning {
    border: 1px solid ${COLORS.warning.main};
    color: ${COLORS.warning.main};

    & .MuiAlert-icon {
      color: ${COLORS.warning.main};
    }
  }

  &.MuiAlert-outlinedInfo {
    border: 1px solid ${COLORS.info.main};
    color: ${COLORS.info.main};

    & .MuiAlert-icon {
      color: ${COLORS.info.main};
    }
  }

  &.MuiAlert-outlinedSuccess {
    border: 1px solid ${COLORS.success.main};
    color: ${COLORS.success.main};

    & .MuiAlert-icon {
      color: ${COLORS.success.main};
    }
  }

  &.MuiAlert-filledError {
    background: ${COLORS.error.main};
  }

  &.MuiAlert-filledWarning {
    background: ${COLORS.warning.main};
  }

  &.MuiAlert-filledInfo {
    background: ${COLORS.info.main};
  }

  &.MuiAlert-filledSuccess {
    background: ${COLORS.success.main};
  }
`

const NovaBaseAlert = styled(NovaAlert)`
  white-space: pre-wrap;
`

export const BaseAlert = createComponentWrapperWithRef<BaseAlertProps>(
  {
    mui: MuiBaseAlert as React.ElementType<BaseAlertProps>,
    nova: NovaBaseAlert as React.ElementType<BaseAlertProps>,
    propsEnhancer(props, componentLibrary) {
      // 'filled' does not exist in Nova, so 'actionable' is the closest match
      if (componentLibrary === 'nova' && props.variant === 'filled') {
        props.variant = 'actionable'
      }

      // Any variant other than 'actionable' is treated as the default
      if (componentLibrary === 'nova' && props.variant !== 'actionable') {
        props.variant = undefined
      }

      // 'actionable' does not exist in MUI, so 'filled' is the closest match
      if (componentLibrary === 'mui' && props.variant === 'actionable') {
        props.variant = 'filled'
      }
      return props
    },
  },
  'BaseAlert',
)

export const AlertTitle = createComponentWrapperWithRef<AlertTitleProps>(
  {
    mui: MuiAlertTitle,
    nova: NovaAlertTitle,
  },
  'AlertTitle',
)
